import "./index.css";

import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import type { ClientRecord, SidebarSection, View } from "./types";
import { seedRecords } from "./data/lookups";
import { fetchCases, createClient, updateClient } from "./api/client";
import { Sidebar } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { AnalyticsDashboard } from "./features/AnalyticsDashboard";
import { ResourcesPage } from "./features/ResourcesPage";
import { ReportsPage } from "./features/ReportsPage";
import { SupportTicketSystem } from "./features/SupportTicketSystem";
import { Dashboard } from "./features/LookupDashboard";
import { ClientWizard } from "./features/wizard/ClientWizard";
import { ProductTour } from "./features/ProductTour";
import { ComingSoon } from "./components/ComingSoon";

export default function FlCareApp() {
  const [view, setView] = useState<View>("dashboard");
  const [records, setRecords] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ClientRecord | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const [tourOpen, setTourOpen] = useState<boolean>(false);
  const [sidebarSection, setSidebarSection] = useState<SidebarSection>("lookup");

  // Load case records from the API on mount. Falls back to the local seed
  // dataset (with a console warning) if the backend isn't reachable yet —
  // handy while the API/DB aren't running during frontend-only development.
  useEffect(() => {
    let cancelled = false;
    fetchCases()
      .then((data) => {
        if (!cancelled) setRecords(data);
      })
      .catch((err) => {
        console.warn("Could not reach FlLink API, using local seed data instead.", err);
        if (!cancelled) {
          setRecords(seedRecords);
          setLoadError("Showing offline sample data — the API is not reachable.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tourTargets = useRef<Record<string, HTMLElement | null>>({});
  function registerTourTarget(key: string) {
    return (el: HTMLElement | null) => {
      tourTargets.current[key] = el;
    };
  }
  function getTourTarget(key: string): HTMLElement | null {
    return tourTargets.current[key] ?? null;
  }

  function openRecord(rec: ClientRecord) {
    setEditing({ ...rec });
    setView("wizard");
  }
  function closeWizard() {
    setView("dashboard");
    setEditing(null);
  }
  async function saveRecord(rec: ClientRecord) {
    try {
      const saved = rec.id ? await updateClient(rec.id, rec) : await createClient(rec);
      setRecords((prev) => {
        const exists = prev.some((r) => r.id === saved.id);
        return exists ? prev.map((r) => (r.id === saved.id ? saved : r)) : [saved, ...prev];
      });
      setToast(`${rec.firstName} ${rec.lastName} was saved successfully.`);
    } catch (err) {
      console.error("Failed to save record", err);
      setToast("Could not save — check that the API is running.");
    }
    setView("dashboard");
    setEditing(null);
    setTimeout(() => setToast(null), 3500);
  }
  function startTour() {
    setView("dashboard");
    setSidebarSection("lookup");
    setSidebarCollapsed(false);
    setTourOpen(true);
  }

  return (
    <div  id="v2-root">
      <div className="flex h-full min-h-[920px] w-full bg-slate-50 text-slate-900" style={{ fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif" }}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
        registerTourTarget={registerTourTarget}
        activeSection={sidebarSection}
        onSelectSection={setSidebarSection}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          onOpenMobileNav={() => setMobileNavOpen(true)}
          records={records}
          onOpenRecord={openRecord}
          onStartTour={startTour}
          registerTourTarget={registerTourTarget}
        />
        <main className="min-w-0 flex-1 overflow-y-auto">
          {loading && <p className="p-6 text-sm text-slate-400">Loading case records…</p>}
          {!loading && loadError && (
            <p className="bg-amber-50 px-6 py-2 text-xs font-medium text-amber-700">{loadError}</p>
          )}
          {!loading && (
            <>
              {sidebarSection === "analytics" && <ComingSoon title="Dashboard" />}
              {sidebarSection === "resources" && <ComingSoon title="Resources" />}
              {sidebarSection === "reports" && <ReportsPage records={records} />}
              {sidebarSection === "systemSupport" && <ComingSoon title="System Support" />}
              {sidebarSection === "lookup" && (
                <>
                  {view === "dashboard" && <Dashboard records={records} onOpenRecord={openRecord} registerTourTarget={registerTourTarget} />}
                  {view === "wizard" && <ClientWizard initial={editing} onCancel={closeWizard} onSave={saveRecord} />}
                </>
              )}
            </>
          )}
        </main>
      </div>
      <ProductTour open={tourOpen} onClose={() => setTourOpen(false)} getTarget={getTourTarget} />
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-xl">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          {toast}
        </div>
      )}
    </div>
    </div>
    
  );
}
